import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { useCart } from "src/hooks/useCart";
import { useCreateCart } from "src/hooks/useCreateCart";
import { useSetCartCustomerEmail } from "src/hooks/useSetCartCustomerEmail";
import { storeConfig } from "src/config/app-config";
import {
  clearGuestSession,
  GuestSession,
  readGuestSession,
  registerGuestMergeFailure,
  rememberGuestAnonymousId,
  rememberGuestCartRef,
  removeLegacyGuestCartKey,
} from "src/utils/helper/guestSession";
import { useMergeCart } from "src/hooks/cart/use-merge-cart";
import { useValidateCartCountry } from "src/hooks/cart/use-validate-cart-country";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import { cartStore } from "src/stores/cart/cart.store";
import { useCartStore } from "src/hooks/stores/use-cart-store";

function getCurrencyCode() {
  return storeConfig.currencyCode;
}

export function CartBootstrap(): null {
  const { data: session, status: sessionStatus } = useSession();
  const { mergeCart } = useMergeCart();
  const { setCartCustomerEmail } = useSetCartCustomerEmail();
  const { validateCartCountry } = useValidateCartCountry();

  const createCarteRef = useRef(true);
  const isCartMergedRef = useRef(false);
  const isEmailSyncedRef = useRef(false);
  // Flips once the guest merge resolved (either way) so the effect below runs
  // again even when a failed merge refetches nothing.
  const [guestMergeSettled, setGuestMergeSettled] = useState(false);

  const { createCart } = useCreateCart({
    onCompleted: (cartData) => {
      cartStore.setCart(cartData?.createMyCart);
      cartStore.setShouldCreateNewCart(false);
    },
  });

  const {
    data: cartData,
    loading: cartQueryLoading,
    hasLoaded,
    refetchCart,
  } = useCart();

  // Hands the remembered guest session to the member account. Keys are cleared
  // only when auth confirms the merge; otherwise we count the failure and give up
  // after a few attempts so a dead cart id cannot block forever.
  const mergeGuestSession = useCallback(
    async ({ cartId, anonymousId }: GuestSession) => {
      const merged = await mergeCart({
        ...(cartId ? { cartId } : {}),
        ...(anonymousId ? { anonymousId } : {}),
      });

      if (merged) {
        clearGuestSession();
      } else {
        registerGuestMergeFailure();
      }

      setGuestMergeSettled(true);
    },
    [mergeCart]
  );

  useEffect(() => {
    cartStore.setLoading(cartQueryLoading);
  }, [cartQueryLoading]);

  useEffect(() => {
    cartStore.registerRefetch(async () => {
      const result = await refetchCart();
      return result.data?.inStore?.me?.activeCart || null;
    });

    return () => cartStore.registerRefetch(null);
  }, [refetchCart]);

  useEffect(() => {
    if (
      cartQueryLoading ||
      !hasLoaded ||
      sessionStatus === "loading" ||
      !session ||
      !session?.accessToken
    ) {
      return;
    }

    const cart = cartData?.cart ?? null;
    const isGuest = session?.user?.signInPlatform === AuthProvider.GUEST_LOGIN;

    removeLegacyGuestCartKey();

    if (isGuest) {
      const anonymousId = cart?.anonymousId ?? session.user?.ct_anonymous_id;

      if (anonymousId) {
        rememberGuestAnonymousId(anonymousId);
      }

      // Only a cart with items is worth merging; an empty cart id would just
      // fail the merge once the guest checks out and the cart is ordered.
      if (cart?.id && (cart.lineItems?.length ?? 0) > 0) {
        rememberGuestCartRef(cart.id);
      }
    }

    if (!isGuest && !isCartMergedRef.current) {
      const guestSession = readGuestSession();

      if (guestSession) {
        isCartMergedRef.current = true;

        void mergeGuestSession(guestSession);

        return;
      }
    }

    if (
      createCarteRef.current &&
      !cart &&
      (!isCartMergedRef.current || guestMergeSettled)
    ) {
      createCarteRef.current = false;
      void createCart({ currency: getCurrencyCode() });
      return;
    }

    if (!cart) {
      return;
    }

    if (cart.id && cart.customerId && session?.user?.email) {
      const sessionEmail = session.user.email.trim().toLowerCase();
      const cartEmail = (cart.customerEmail ?? "").trim().toLowerCase();

      if (
        !isEmailSyncedRef.current &&
        (!cartEmail || cartEmail !== sessionEmail)
      ) {
        isEmailSyncedRef.current = true;
        void setCartCustomerEmail(sessionEmail, {
          id: cart.id,
          version: cart.version as number,
        });
      }
    }

    void validateCartCountry(cart);

    cartStore.setCart(cart);
  }, [
    cartData?.cart,
    cartQueryLoading,
    hasLoaded,
    sessionStatus,
    session,
    session?.accessToken,
    session?.user?.email,
    createCart,
    setCartCustomerEmail,
    mergeGuestSession,
    guestMergeSettled,
    validateCartCountry,
  ]);

  const shouldCreateNewCart = useCartStore(
    (state) => state.shouldCreateNewCart
  );

  useEffect(() => {
    if (!shouldCreateNewCart) {
      return;
    }

    void createCart({ currency: getCurrencyCode() });
  }, [shouldCreateNewCart, createCart]);

  return null;
}
